/**
 * Render IRGraph as a draw.io Flowchart (standard flowchart shapes, no UML activity markers).
 */

import type { LayoutResult, IRNode, IRGraph, ColorPalette } from '../types';
import { NodeShape } from '../types';
import { SHAPE_REGISTRY, EDGE_STYLE, EDGE_STYLE_DASHED } from '../registry';
import { getShapeColors, applyColors } from '../color';
import { IdGen, wrapMxfile, vertexCell, edgeCell, labelCell } from './xml-builder';

export function renderDrawioFlowchart(
  layout: LayoutResult,
  palette: ColorPalette,
): string {
  const gen = new IdGen();
  const graph = layout.graph;
  const cells: string[] = [];

  const nodeCellMap = new Map<string, string>();

  // Render all nodes (flat, no swimlane containers)
  for (const [nid, node] of graph.nodes) {
    if (node.x === undefined || node.y === undefined) continue;

    const cellId = gen.next();
    nodeCellMap.set(nid, cellId);

    let style = getFlowchartStyle(node);
    const colors = getShapeColors(node.shape, palette);
    style = applyColors(style, colors);

    const lane = node.laneId ? graph.lanes.find((l) => l.id === node.laneId) : null;
    cells.push(
      vertexCell(
        cellId,
        node.label,
        style,
        '1',
        layout.containerX + (lane?.x ?? 0) + (node.x ?? 0),
        layout.containerY + (lane?.y ?? 0) + (node.y ?? 0),
        node.width,
        node.height,
      ),
    );
  }

  // Render edges
  for (const edge of graph.edges) {
    const srcCell = nodeCellMap.get(edge.source);
    const tgtCell = nodeCellMap.get(edge.target);
    if (!srcCell || !tgtCell) continue;

    let style = edge.dashed ? EDGE_STYLE_DASHED : EDGE_STYLE;

    // Apply pre-computed decision exit points
    const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
    if (exitPt) {
      style += `exitX=${exitPt.x};exitY=${exitPt.y};exitDx=0;exitDy=0;`;
    }

    // Apply entry points (e.g. back-edge routing)
    const entryPt = edge.meta?.entryPoint as { x: number; y: number } | undefined;
    if (entryPt) {
      style += `entryX=${entryPt.x};entryY=${entryPt.y};entryDx=0;entryDy=0;`;
    }

    const edgeId = gen.next();
    const routePoints = edge.meta?.routePoints as Array<{ x: number; y: number }> | undefined;
    const adjusted = routePoints ? adjustRoutePoints(routePoints, edge, graph, layout) : undefined;
    cells.push(edgeCell(edgeId, '', style, '1', srcCell, tgtCell, adjusted));

    if (edge.label) {
      const lblId = gen.next();
      cells.push(labelCell(lblId, edge.label, edgeId));
    }
  }

  return wrapMxfile(cells);
}

function getFlowchartStyle(node: IRNode): string {
  const entry = SHAPE_REGISTRY[node.shape];
  return entry.flowchartStyle ?? entry.activityStyle ?? 'rounded=0;whiteSpace=wrap;html=1;';
}

/**
 * Convert route points from edge-rule space to root space.
 * Edge rules produce lane-relative coords for intra-lane edges and
 * graph-absolute coords for cross-lane edges. Since the flowchart renderer
 * places everything at root level (parent="1"), we must add the container
 * offset and, for intra-lane edges, the lane offset.
 */
function adjustRoutePoints(
  routePoints: Array<{ x: number; y: number }>,
  edge: { crossLane?: boolean; source: string },
  graph: IRGraph,
  layout: LayoutResult,
): Array<{ x: number; y: number }> {
  let offX = layout.containerX;
  let offY = layout.containerY;
  if (!edge.crossLane) {
    const srcNode = graph.nodes.get(edge.source);
    if (srcNode?.laneId) {
      const lane = graph.lanes.find((l) => l.id === srcNode.laneId);
      if (lane) {
        offX += lane.x;
        offY += lane.y;
      }
    }
  }
  return routePoints.map((p) => ({ x: p.x + offX, y: p.y + offY }));
}
